import { Tab, Tabs } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import type { SyntheticEvent } from 'react';
import { useLocation, useNavigate } from 'react-router';
import SidebarSpriteIcon from '../../../icons/SidebarSpriteIcon';
import useLocalization from '../../../../hooks/useLocalization';
import useProductAvailability from '../../../../hooks/useProductAvailability';
import { StyleObj } from '../../../../@types';

const GOLA_TABS = [
  { label: 'sports', path: '/sports', iconName: 'sports' as const, shouldNavigate: false },
  { label: 'inPlay', path: '/in-play', iconName: 'live' as const, shouldNavigate: true },
  { label: 'gaming', path: '/casino', iconName: 'casino' as const, shouldNavigate: true },
];

const ACCOUNT_PATH_PREFIX = '/my-account';

type GolaTab = (typeof GOLA_TABS)[number];

const styles: StyleObj = {
  tabs: {
    minHeight: 48,
    borderBottom: 1,
    borderColor: 'neutral.700',
    '& .MuiTab-root': {
      color: 'neutral.400',
      p: 0.5,
      minWidth: 0,
      flex: 1,
      textTransform: 'none',
      typography: 'body2',
      display: 'flex',
      flexDirection: 'column',
      '&.Mui-selected': {
        color: 'info.main',
      },
    },
    '& .MuiTab-icon': {
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center',
      minHeight: 27,
      marginBottom: 0.5,
    },
    '& .MuiSvgIcon-root': {
      fontSize: 18,
    },
    '& .MuiTabs-indicator': {
      display: 'none',
    },
  },
};

const getVisibleTabs = (hasSportsbookAccess: boolean, hasCasinoAccess: boolean): GolaTab[] =>
  GOLA_TABS.filter((tab) => {
    if (tab.path === '/casino') {
      return hasCasinoAccess;
    }

    return hasSportsbookAccess;
  });

const SidebarTabs = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { getLocalizedConfig } = useLocalization();
  const { hasSportsbookAccess, hasCasinoAccess } = useProductAvailability();

  const tabs = useMemo<GolaTab[]>(
    () => getVisibleTabs(hasSportsbookAccess, hasCasinoAccess),
    [hasCasinoAccess, hasSportsbookAccess]
  );
  const localizedTabs = useMemo<GolaTab[]>(() => getLocalizedConfig(tabs), [getLocalizedConfig, tabs]);

  const isAccountPage = location.pathname.startsWith(ACCOUNT_PATH_PREFIX);
  const defaultValue = isAccountPage ? -1 : tabs.findIndex((tab) => location.pathname.startsWith(tab.path));
  const [value, setValue] = useState(defaultValue);

  useEffect(() => {
    setValue(isAccountPage ? -1 : (tabs.findIndex((tab) => location.pathname.startsWith(tab.path)) ?? -1));
  }, [location.pathname, isAccountPage, tabs]);

  const handleChange = (_: SyntheticEvent, newValue: number) => {
    setValue(newValue);

    const tab = tabs[newValue];
    if (tab.shouldNavigate) {
      navigate(tab.path);
    }
  };

  if (tabs.length === 0) {
    return null;
  }

  return (
    <Tabs value={value} onChange={handleChange} sx={styles.tabs} variant='fullWidth'>
      {localizedTabs.map((tab, index) => (
        <Tab
          key={tab.path}
          value={index}
          icon={tab.iconName ? <SidebarSpriteIcon name={tab.iconName} selected={value === index} /> : undefined}
          iconPosition='top'
          label={tab.label}
          onClick={(e) => handleChange(e, index)}
        />
      ))}
    </Tabs>
  );
};

export default SidebarTabs;
